import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import { ServiceHealthStatus } from '@/types/api';
import { useAccountStore } from '@nimbitlabs/features';

export interface ServiceHealthOverview {
  id: string;
  serviceId: string;
  serviceName: string;
  environmentId: string;
  environmentName: string;
  status: ServiceHealthStatus;
  lastHealthCheckAt?: string | null;
  consecutiveHealthFailures: number;
  isEnabled: boolean;
  url: string;
}

interface ServiceHealthState {
  healthData: ServiceHealthOverview[];
  loading: boolean;
  error: string | null;
  
  fetchHealth: () => Promise<void>;
  getHealthByService: (serviceId: string) => ServiceHealthOverview[];
  getHealthByEnvironment: (environmentId: string) => ServiceHealthOverview[];
  getOverallHealth: () => {
    healthy: number;
    unhealthy: number;
    unknown: number;
    healthyPercentage: number;
    unhealthyPercentage: number;
    unknownPercentage: number;
  };
  triggerHealthCheck: () => Promise<void>;
  clearError: () => void;
}

const serviceHealthStore = create<ServiceHealthState>()(
  subscribeWithSelector((set, get) => ({
  healthData: [],
  loading: false,
  error: null,
  
  fetchHealth: async () => {
    set({ loading: true, error: null });
    try {
      const configs = await api.serviceMonitoringConfigurations.getAll();
      const healthData: ServiceHealthOverview[] = configs.map(config => ({
        id: config.id,
        serviceId: config.serviceId,
        serviceName: config.serviceName,
        environmentId: config.environmentId,
        environmentName: config.environmentName,
        status: config.currentHealthStatus,
        lastHealthCheckAt: config.lastHealthCheckAt,
        consecutiveHealthFailures: config.consecutiveHealthFailures,
        isEnabled: config.isEnabled,
        url: config.url,
      }));
      set({ healthData, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch service health', loading: false });
    }
  },
  
  getHealthByService: (serviceId: string) => {
    return get().healthData.filter(h => h.serviceId === serviceId);
  },
  
  getHealthByEnvironment: (environmentId: string) => {
    return get().healthData.filter(h => h.environmentId === environmentId);
  },
  
  getOverallHealth: () => {
    const healthData = get().healthData;
    const total = healthData.length;
    const healthy = healthData.filter(h => h.status === ServiceHealthStatus.Healthy).length;
    const unhealthy = healthData.filter(h => h.status === ServiceHealthStatus.Unhealthy || h.status === ServiceHealthStatus.Degraded).length;
    const unknown = total - healthy - unhealthy;
    
    return {
      healthy,
      unhealthy,
      unknown,
      healthyPercentage: total ? Math.round((healthy / total) * 100) : 0,
      unhealthyPercentage: total ? Math.round((unhealthy / total) * 100) : 0,
      unknownPercentage: total ? Math.round((unknown / total) * 100) : 0,
    };
  },
  
  triggerHealthCheck: async () => {
    await get().fetchHealth();
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useServiceHealthStore = serviceHealthStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      serviceHealthStore.getState().fetchHealth();
      // Set up polling for real-time health updates (every 30 seconds)
      const interval = setInterval(() => {
        serviceHealthStore.getState().fetchHealth();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      serviceHealthStore.setState({ healthData: [] });
    }
  }
);
